import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

type State<T> = { data: T | null; loading: boolean; error: string | null };

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e?.response?.data?.message || 'Could not load data.';
}

/** GET `url` on mount and whenever it changes. `pollMs` keeps refreshing silently (e.g. while AI runs). */
export function useApi<T>(url: string | null, pollMs?: number) {
  const [state, setState] = useState<State<T>>({ data: null, loading: Boolean(url), error: null });
  const alive = useRef(true);

  const load = useCallback(
    async (silent = false) => {
      if (!url) return;
      if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const { data } = await api.get<T>(url);
        if (alive.current) setState({ data, loading: false, error: null });
      } catch (err) {
        if (alive.current) setState((s) => ({ data: silent ? s.data : null, loading: false, error: errorMessage(err) }));
      }
    },
    [url],
  );

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  useEffect(() => {
    if (!pollMs) return;
    const t = setInterval(() => void load(true), pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  return { ...state, reload: load };
}
